import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/styles';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography
} from '@material-ui/core';
import {
  EMAIL_SUBSCRIPTION_CARD,
  NAME_SUBSCRIPTION_CARD,
  STRIPE_PAYMENT_STATUS_SUCCEEDED
} from '../../config';
import { green } from '@material-ui/core/colors';
import { Check } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4)
  },
  cardElement: {
    margin: 20,
    color: theme.palette.text.primary
  },
  cardDetailsSection: {
    marginBottom: theme.spacing(4),
    color: theme.palette.text.primary
  },
  cardInputSection: {
    border: `solid 1px ${theme.palette.divider}`,
    marginBottom: 40,
    marginTop: 20
  },
  payBtn: {
    textTransform: 'none',
    fontSize: '20px'
  },
  confirmPayBtn: {
    textTransform: 'none',
    fontSize: '20px'
  },
  bundleSection: {
    marginTop: '40px',
    marginBottom: '30px'
  },
  textField: {
    marginLeft: 0,
    marginRight: 0,
    marginBottom: theme.spacing(4)
  },
  totalAmount: {},
  buttonProgress: {
    color: green[500]
  },
  inputName: {
    textTransform: 'uppercase',
    marginBottom: 5
  },
  headerCard: {
    textAlign: 'center',
    textTransform: 'uppercase'
  },
  headerSectionView: {
    marginBottom: 10,
    padding: 20
  }
}));

const schema = {};
schema[EMAIL_SUBSCRIPTION_CARD] = {
  presence: { allowEmpty: false, message: 'is required' },
  email: true,
  length: {
    maximum: 64
  }
};

schema[NAME_SUBSCRIPTION_CARD] = {
  presence: { allowEmpty: false, message: 'is required' },
  length: {
    maximum: 200,
    minimum: 2
  }
};

const defaultPlanInit = {};
defaultPlanInit[NAME_SUBSCRIPTION_CARD] = '';

const CardForm = ({
  totalPrice,
  onClickPay,
  emailInvoice,
  subscription,
  setMessageApp,
  isLoading,
  onPaymentSuccess,
  payLabel
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  const [formState, setFormState] = useState({ defaultPlanInit });

  const options = {
    style: {
      base: {
        color: theme.palette.text.primary,
        fontSize: '16px',
        letterSpacing: '0.025em',
        fontFamily: 'Source Code Pro, monospace',
        '::placeholder': {
          color: theme.palette.type === 'dark' ? '#aab7c4' : '#7f8c9a'
        }
      },
      invalid: {
        color: theme.palette.error.main
      }
    },
    hidePostalCode: true
  };

  const handleChangeName = (event) => {
    event.persist();
    setFormState({ ...formState, name: event.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    stripe
      .createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          email: emailInvoice,
          name: formState[NAME_SUBSCRIPTION_CARD]
        }
      })
      .then((newPaymentMethod) => {
        if (newPaymentMethod.error) {
          console.log(newPaymentMethod.error.message);
        } else {
          onClickPay(newPaymentMethod.paymentMethod);
        }
      })
      .catch((error) => {
        setMessageApp(
          'Something went wrong. Please try another payment method.',
          'error'
        );
      });
  };
  return (
    <>
      <Card className={classes.headerSectionView} elevation={0}>
        <Typography variant="h5" align="center">
          PAYMENT METHOD
        </Typography>
      </Card>
      <Card className={classes.root}>
        <Grid container spacing={3} direction="row" justifyContent="center">
          <Grid item lg={12} md={12} xl={12} xs={12}>
            <form onSubmit={handleSubmit}>
              <CardContent>
                <div className={classes.cardDetailsSection}>
                  <Typography className={classes.inputName} variant="h6">
                    Cardholder Name
                  </Typography>
                  <TextField
                    data-test="name-card-form"
                    className={classes.textField}
                    fullWidth
                    InputLabelProps={{
                      shrink: true
                    }}
                    label=""
                    margin="normal"
                    name={NAME_SUBSCRIPTION_CARD}
                    onChange={handleChangeName}
                    required
                    value={
                      formState && formState[NAME_SUBSCRIPTION_CARD]
                        ? formState[NAME_SUBSCRIPTION_CARD]
                        : ''
                    }
                    variant="outlined"
                  />
                  <Typography className={classes.inputName} variant="h6">
                    Card Number - Expiration Date - CVC
                  </Typography>
                  <div className={classes.cardInputSection}>
                    <CardElement
                      id="number-expiration-cvc-card-form"
                      data-test="number-expiration-cvc-card-form"
                      options={options}
                      className={classes.cardElement}
                    />
                  </div>
                  {!subscription && !isLoading && (
                    <Button
                      variant="contained"
                      color="primary"
                      data-test="pay-btn-card-form"
                      className={classes.payBtn}
                      type="submit"
                      disabled={!stripe || isLoading}
                      fullWidth>
                      Order and Pay
                    </Button>
                  )}
                </div>
              </CardContent>
            </form>
          </Grid>
          {isLoading && (
            <Grid item lg={1} md={1} xl={1} xs={1}>
              <CircularProgress size={60} className={classes.buttonProgress} />
            </Grid>
          )}
          {subscription &&
            subscription.status === STRIPE_PAYMENT_STATUS_SUCCEEDED && (
              <Grid item lg={12} md={12} xl={12} xs={12}>
                <Button
                  onClick={onPaymentSuccess}
                  startIcon={<Check />}
                  data-test="sing-in-login"
                  color="primary"
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained">
                  Go to Dashboard
                </Button>
              </Grid>
            )}
        </Grid>
      </Card>
    </>
  );
};

CardForm.defaultProps = {
  totalPrice: 0,
  payLabel: ''
};

CardForm.propTypes = {
  totalPrice: PropTypes.number,
  onClickPay: PropTypes.func.isRequired,
  subscription: PropTypes.object,
  setMessageApp: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onPaymentSuccess: PropTypes.func,
  payLabel: PropTypes.string
};

export default CardForm;
